import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import TopupFeePreview from "@/components/TopupFeePreview";
import { toast } from "sonner";
import {
  Wallet, Plus, CreditCard, Smartphone, Search, Loader2,
  IndianRupee, ArrowDown, ArrowUp, Trash2, ShieldCheck, AlertCircle,
} from "lucide-react";

const NETWORKS = ["visa", "mastercard", "rupay", "amex"];
const CARD_TYPES = ["credit", "debit", "prepaid"];

export default function CustomerWallet() {
  const [searchMobile, setSearchMobile] = useState("");
  const [mobile, setMobile] = useState("");
  const [info, setInfo] = useState(null);
  const [methods, setMethods] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(false);

  // top-up dialog
  const [topupOpen, setTopupOpen] = useState(false);
  const [topupAmount, setTopupAmount] = useState(100);
  const [topupGateway, setTopupGateway] = useState("");
  const [topupPg, setTopupPg] = useState("");
  const [pgFeasibility, setPgFeasibility] = useState(null);

  // Load PG feasibility on mount
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/customer-wallet/available-pgs");
        setPgFeasibility(data);
        const first = (data.gateways || []).find((g) => g.allowed);
        if (first) {
          setTopupGateway(first.key);
          const firstCh = first.supports_channel ? (first.channels || []).find((c) => c.allowed && c.active)?.id || "" : "";
          setTopupPg(firstCh);
        }
      } catch { /* silent */ }
    })();
  }, []); // eslint-disable-line

  // add method dialog
  const [methodOpen, setMethodOpen] = useState(false);
  const [methodType, setMethodType] = useState("card");
  const [last4, setLast4] = useState(""); const [issuer, setIssuer] = useState("");
  const [linkedMobile, setLinkedMobile] = useState(""); const [cardType, setCardType] = useState("credit");
  const [cardNetwork, setCardNetwork] = useState("visa");
  const [upiId, setUpiId] = useState(""); const [holderName, setHolderName] = useState("");
  const [nickname, setNickname] = useState("");
  const [pendingTopups, setPendingTopups] = useState([]);
  const [allTopups, setAllTopups] = useState([]);
  const [topupStats, setTopupStats] = useState({});

  const load = async (m) => {
    if (!/^[6-9]\d{9}$/.test(m)) { toast.error("Enter a valid 10-digit mobile"); return; }
    setLoading(true);
    try {
      const [bal, meth, led, tp] = await Promise.all([
        api.get(`/customer-wallet/balance/${m}`),
        api.get(`/customer-wallet/payment-methods/${m}`),
        api.get(`/customer-wallet/ledger/${m}`),
        api.get(`/outbound-txns/customer-wallet/${m}/topups?limit=200`).catch(() => ({ data: { items: [], stats: {} } })),
      ]);
      setInfo(bal.data);
      setMethods(meth.data.items || []);
      setLedger(led.data.items || []);
      const topups = tp.data.items || [];
      setAllTopups(topups);
      setTopupStats(tp.data.stats || {});
      setPendingTopups(topups.filter((t) => {
        const s = (t.status || "").toLowerCase();
        return !(s === "credited" || s === "success" || s === "failed");
      }));
      setMobile(m);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load");
    } finally { setLoading(false); }
  };

  const submitTopup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/customer-wallet/topup/order", {
        mobile, amount: parseFloat(topupAmount), gateway: topupGateway, pg: topupPg || undefined,
      });
      const url = data.sdk_params?.checkout_url;
      if (url) {
        window.open(url, "_blank", "width=480,height=720");
        toast.success("Checkout opened — refresh balance after payment");
      } else {
        toast.success("Top-up order created");
      }
      setTopupOpen(false);
      setTimeout(() => load(mobile), 800);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Top-up failed");
    } finally { setLoading(false); }
  };

  const submitMethod = async () => {
    try {
      if (methodType === "card") {
        if (!/^\d{4}$/.test(last4)) { toast.error("Last 4 digits required"); return; }
        if (!issuer) { toast.error("Issuer required"); return; }
        if (!/^[6-9]\d{9}$/.test(linkedMobile)) { toast.error("Linked mobile invalid"); return; }
        await api.post("/customer-wallet/payment-methods/card", {
          mobile, type: "card", last4, issuer, linked_mobile: linkedMobile,
          card_type: cardType, card_network: cardNetwork, nickname,
        });
      } else {
        if (!/^[\w.\-]+@[\w]+$/.test(upiId)) { toast.error("Enter a valid UPI ID (name@bank)"); return; }
        if (!holderName) { toast.error("Holder name required"); return; }
        await api.post("/customer-wallet/payment-methods/upi", {
          mobile, type: "upi", upi_id: upiId, holder_name: holderName, nickname,
        });
      }
      toast.success("Payment method added");
      setMethodOpen(false);
      setLast4(""); setIssuer(""); setLinkedMobile(""); setUpiId(""); setHolderName(""); setNickname("");
      load(mobile);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const deleteMethod = async (id) => {
    if (!window.confirm("Remove this payment method?")) return;
    try {
      await api.delete(`/customer-wallet/payment-methods/${id}`);
      toast.success("Removed");
      load(mobile);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
  };

  return (
    <DashboardLayout>
      <div>
        <span className="overline">Customer Operations</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2 flex items-center gap-2" data-testid="page-title">
          <Wallet className="h-7 w-7 text-primary" strokeWidth={1.5} /> Customer wallet
        </h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">
          Top-up a customer's wallet, manage their whitelisted payment methods, and view txn history. Every BBPS / Payout debits this wallet.
        </p>
      </div>

      {/* Search */}
      <div className="mt-6 surface-card p-4 flex items-center gap-3 max-w-xl">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          data-testid="customer-mobile-search"
          value={searchMobile}
          onChange={(e) => setSearchMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
          onKeyDown={(e) => e.key === "Enter" && load(searchMobile)}
          placeholder="Enter customer mobile (10 digits)"
          className="font-mono"
        />
        <Button onClick={() => load(searchMobile)} disabled={loading} data-testid="customer-search-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Look up"}
        </Button>
      </div>

      {info && (
        <>
          {/* Balance + actions */}
          <div className="mt-6 surface-card p-6 grid sm:grid-cols-3 gap-4 items-center" data-testid="balance-card">
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground">Balance · +91 {info.mobile}</div>
              <div className="font-display font-bold text-4xl mt-1 flex items-center" data-testid="balance-value">
                <IndianRupee className="h-6 w-6" strokeWidth={2} />{info.balance.toFixed(2)}
              </div>
              <div className="mt-2 flex items-center gap-2 text-xs">
                <Badge variant={info.kyc_status === "verified" ? "default" : "outline"} className={info.kyc_status === "verified" ? "bg-emerald-100 text-emerald-700" : ""}>
                  <ShieldCheck className="h-3 w-3 mr-1" /> KYC: {info.kyc_status}
                </Badge>
                {info.kyc_status !== "verified" && info.rules?.kyc_unverified_max_balance && (
                  <span className="text-muted-foreground">Cap ₹{info.rules.kyc_unverified_max_balance}</span>
                )}
              </div>
            </div>
            <div className="sm:col-span-2 flex gap-2 justify-end flex-wrap">
              <Button onClick={() => setTopupOpen(true)} data-testid="topup-btn">
                <Plus className="h-4 w-4 mr-2" /> Top-up
              </Button>
              <Button variant="outline" onClick={() => setMethodOpen(true)} data-testid="add-method-btn">
                <CreditCard className="h-4 w-4 mr-2" /> Add payment method
              </Button>
              {info.kyc_status !== "verified" && (
                <Button variant="outline" onClick={() => window.location.href = `/customer-kyc?mobile=${info.mobile}`}>
                  <ShieldCheck className="h-4 w-4 mr-2" /> Verify KYC
                </Button>
              )}
            </div>
          </div>

          {/* Pending top-ups */}
          {pendingTopups.length > 0 && (
            <div className="mt-4 surface-card p-4" data-testid="pending-topups">
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">Pending top-ups ({pendingTopups.length})</div>
              <div className="space-y-2">
                {pendingTopups.map((t) => (
                  <div key={t.id} className="flex items-center justify-between rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
                    <div>
                      <span className="font-mono font-semibold">{formatINR(t.amount)}</span>
                      <span className="text-muted-foreground ml-2">{t.gateway}</span>
                      {t.pg && <span className="text-muted-foreground ml-1">· {t.pg}</span>}
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-amber-700 border-amber-300 text-[10px]">{t.status || "pending"}</Badge>
                      <span className="text-[10px] text-muted-foreground">{new Date(t.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false })}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabs: methods + ledger + topup history */}
          <Tabs defaultValue="methods" className="mt-6">
            <TabsList>
              <TabsTrigger value="methods" data-testid="tab-methods">Payment methods ({methods.length})</TabsTrigger>
              <TabsTrigger value="topups" data-testid="tab-topups">
                Top-ups ({allTopups.length})
                {topupStats.pending_count > 0 && (
                  <Badge variant="outline" className="ml-1.5 text-[9px] text-amber-700 border-amber-300">{topupStats.pending_count} pending</Badge>
                )}
              </TabsTrigger>
              <TabsTrigger value="ledger" data-testid="tab-ledger">Transaction history</TabsTrigger>
            </TabsList>
            <TabsContent value="methods" className="mt-4 surface-card p-4" data-testid="methods-pane">
              {methods.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm">
                  <CreditCard className="h-8 w-8 mx-auto mb-2 opacity-50" /> No payment methods yet — add one above.
                </div>
              ) : (
                <div className="space-y-2">
                  {methods.map((m) => (
                    <div key={m.id} className="flex items-center gap-3 p-3 border rounded-md" data-testid={`method-${m.id}`}>
                      {m.type === "card" ? <CreditCard className="h-5 w-5 text-primary" /> : <Smartphone className="h-5 w-5 text-primary" />}
                      <div className="flex-1">
                        {m.type === "card" ? (
                          <div className="text-sm font-medium">{m.issuer} ••••{m.last4} <span className="text-xs text-muted-foreground">· {m.card_network} · {m.card_type}</span></div>
                        ) : (
                          <div className="text-sm font-medium">{m.upi_id} <span className="text-xs text-muted-foreground">· {m.holder_name}</span></div>
                        )}
                        {m.nickname && <div className="text-xs text-muted-foreground">{m.nickname}</div>}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => deleteMethod(m.id)} data-testid={`delete-${m.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
            <TabsContent value="topups" className="mt-4 surface-card overflow-hidden" data-testid="topups-pane">
              {allTopups.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm">No top-ups yet.</div>
              ) : (
                <>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 p-3 bg-accent/30 border-b text-xs">
                    <div><span className="text-muted-foreground">Total: </span><b>{topupStats.total || 0}</b></div>
                    <div><span className="text-emerald-700">Success: </span><b>{topupStats.success_count || 0}</b> <span className="text-muted-foreground font-mono text-[10px]">{formatINR(topupStats.success_amount || 0)}</span></div>
                    <div><span className="text-amber-700">Pending: </span><b>{topupStats.pending_count || 0}</b> <span className="text-muted-foreground font-mono text-[10px]">{formatINR(topupStats.pending_amount || 0)}</span></div>
                    <div><span className="text-rose-700">Failed: </span><b>{topupStats.failed_count || 0}</b></div>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-accent/50 text-[11px] uppercase tracking-widest text-muted-foreground">
                      <tr>
                        <th className="px-3 py-2 text-left">When</th>
                        <th className="px-3 py-2 text-right">Amount</th>
                        <th className="px-3 py-2 text-left">Gateway/PG</th>
                        <th className="px-3 py-2 text-left">Status</th>
                        <th className="px-3 py-2 text-left">Provider txnid</th>
                        <th className="px-3 py-2 text-left">UTR / Payment ID</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {allTopups.map((t) => {
                        const s = (t.status || "").toLowerCase();
                        const cls =
                          s === "credited" || s === "success" ? "text-emerald-700 border-emerald-300 bg-emerald-50" :
                          s === "failed" ? "text-rose-700 border-rose-300 bg-rose-50" :
                          "text-amber-700 border-amber-300 bg-amber-50";
                        return (
                          <tr key={t.id} data-testid={`topup-${t.id}`}>
                            <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                              {new Date(t.created_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", hour12: false })}
                            </td>
                            <td className="px-3 py-2 text-right font-mono font-semibold">{formatINR(t.amount)}</td>
                            <td className="px-3 py-2 text-xs">{t.gateway}{t.pg ? ` · ${t.pg}` : ""}</td>
                            <td className="px-3 py-2"><Badge variant="outline" className={`text-[10px] ${cls}`}>{t.status}</Badge></td>
                            <td className="px-3 py-2 text-xs font-mono text-muted-foreground">{t.provider_order_id || "—"}</td>
                            <td className="px-3 py-2 text-xs font-mono text-muted-foreground truncate max-w-[160px]">{t.provider_payment_id || t.provider_ref || "—"}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </>
              )}
            </TabsContent>
            <TabsContent value="ledger" className="mt-4 surface-card overflow-hidden" data-testid="ledger-pane">
              {ledger.length === 0 ? (
                <div className="py-8 text-center text-muted-foreground text-sm">No transactions yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-accent/50 text-[11px] uppercase tracking-widest text-muted-foreground">
                    <tr><th className="px-3 py-2 text-left">When</th><th className="px-3 py-2 text-left">Type</th><th className="px-3 py-2 text-left">Reason</th><th className="px-3 py-2 text-right">Amount</th><th className="px-3 py-2 text-right">Balance</th></tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {ledger.map((l) => (
                      <tr key={l.id} data-testid={`ledger-${l.id}`}>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{new Date(l.created_at).toLocaleString()}</td>
                        <td className="px-3 py-2">
                          {l.type === "credit"
                            ? <span className="inline-flex items-center gap-1 text-emerald-700"><ArrowDown className="h-3 w-3" /> Credit</span>
                            : <span className="inline-flex items-center gap-1 text-rose-700"><ArrowUp className="h-3 w-3" /> Debit</span>}
                        </td>
                        <td className="px-3 py-2 text-xs">{l.reason}</td>
                        <td className={`px-3 py-2 text-right font-mono ${l.type === "credit" ? "text-emerald-700" : "text-rose-700"}`}>{l.type === "credit" ? "+" : "−"}₹{l.amount.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right font-mono">₹{l.balance_after.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}

      {/* Top-up dialog */}
      <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
        <DialogContent data-testid="topup-dialog">
          <DialogHeader><DialogTitle>Top-up customer wallet</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Amount (₹)</Label>
              <Input type="number" value={topupAmount} onChange={(e) => setTopupAmount(e.target.value)} className="font-mono mt-1" data-testid="topup-amount" />
              {info && info.kyc_status !== "verified" && (
                <div className="mt-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-900 flex gap-2 items-start">
                  <AlertCircle className="h-3 w-3 mt-0.5" />
                  <span>KYC pending — wallet capped at ₹{info.rules?.kyc_unverified_max_balance || 100}.</span>
                </div>
              )}
            </div>
            <div>
              <Label>Gateway</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {(pgFeasibility?.gateways || []).map((g) => (
                  <button
                    type="button"
                    key={g.key}
                    disabled={!g.allowed}
                    onClick={() => {
                      setTopupGateway(g.key);
                      const firstCh = g.supports_channel ? (g.channels || []).find((c) => c.allowed && c.active)?.id || "" : "";
                      setTopupPg(firstCh);
                    }}
                    data-testid={`topup-gw-${g.key}`}
                    className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                      topupGateway === g.key && g.allowed
                        ? "border-primary bg-primary/10 text-primary"
                        : g.allowed
                          ? "border-border hover:bg-accent"
                          : "border-border bg-muted/40 text-muted-foreground cursor-not-allowed"
                    }`}
                  >
                    {g.label}
                    {!g.allowed && <div className="text-[10px] opacity-70">not in package</div>}
                  </button>
                ))}
              </div>
            </div>
            {/* PG Channel dropdown — only for gateways with channels */}
            {(() => {
              const gwEntry = (pgFeasibility?.gateways || []).find((g) => g.key === topupGateway);
              if (!gwEntry?.supports_channel || !gwEntry.channels?.length) return null;
              const allowedChannels = gwEntry.channels.filter((c) => c.active);
              if (allowedChannels.length === 0) return null;
              return (
                <div>
                  <Label>PG Channel</Label>
                  <Select value={topupPg} onValueChange={setTopupPg}>
                    <SelectTrigger className="mt-1 font-mono" data-testid="topup-pg-channel"><SelectValue placeholder="Select channel" /></SelectTrigger>
                    <SelectContent>
                      {allowedChannels.map((c) => (
                        <SelectItem key={c.id} value={c.id} disabled={!c.allowed}>
                          {c.label}{!c.allowed ? " (locked)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })()}
            <TopupFeePreview
              amount={topupAmount}
              gateway={topupGateway}
              channel={topupPg}
              testidPrefix="cw-topup-fee"
            />
          </div>
          <DialogFooter>
            <Button onClick={submitTopup} disabled={loading} data-testid="topup-submit">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              Continue to gateway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add method dialog */}
      <Dialog open={methodOpen} onOpenChange={setMethodOpen}>
        <DialogContent className="max-w-lg" data-testid="add-method-dialog">
          <DialogHeader><DialogTitle>Add payment method (whitelist)</DialogTitle></DialogHeader>
          <Tabs value={methodType} onValueChange={setMethodType}>
            <TabsList className="grid grid-cols-2"><TabsTrigger value="card" data-testid="method-tab-card">Card</TabsTrigger><TabsTrigger value="upi" data-testid="method-tab-upi">UPI</TabsTrigger></TabsList>
            <TabsContent value="card" className="space-y-3 mt-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Last 4 digits</Label><Input value={last4} onChange={(e) => setLast4(e.target.value.replace(/\D/g, "").slice(0, 4))} className="font-mono mt-1" data-testid="method-last4" /></div>
                <div><Label>Issuer (Bank)</Label><Input value={issuer} onChange={(e) => setIssuer(e.target.value)} placeholder="HDFC Bank" className="mt-1" data-testid="method-issuer" /></div>
                <div><Label>Linked mobile</Label><Input value={linkedMobile} onChange={(e) => setLinkedMobile(e.target.value.replace(/\D/g, "").slice(0, 10))} className="font-mono mt-1" data-testid="method-linked-mobile" /></div>
                <div>
                  <Label>Card type</Label>
                  <Select value={cardType} onValueChange={setCardType}><SelectTrigger className="mt-1" data-testid="method-card-type"><SelectValue /></SelectTrigger><SelectContent>{CARD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent></Select>
                </div>
                <div>
                  <Label>Network</Label>
                  <Select value={cardNetwork} onValueChange={setCardNetwork}><SelectTrigger className="mt-1" data-testid="method-network"><SelectValue /></SelectTrigger><SelectContent>{NETWORKS.map((n) => <SelectItem key={n} value={n}>{n.toUpperCase()}</SelectItem>)}</SelectContent></Select>
                </div>
                <div><Label>Nickname (optional)</Label><Input value={nickname} onChange={(e) => setNickname(e.target.value)} className="mt-1" /></div>
              </div>
            </TabsContent>
            <TabsContent value="upi" className="space-y-3 mt-3">
              <div><Label>UPI ID</Label><Input value={upiId} onChange={(e) => setUpiId(e.target.value.toLowerCase())} placeholder="name@upi" className="font-mono mt-1" data-testid="method-upi-id" /></div>
              <div><Label>Holder name</Label><Input value={holderName} onChange={(e) => setHolderName(e.target.value)} className="mt-1" data-testid="method-upi-name" /></div>
              <div><Label>Nickname (optional)</Label><Input value={nickname} onChange={(e) => setNickname(e.target.value)} className="mt-1" /></div>
            </TabsContent>
          </Tabs>
          <DialogFooter>
            <Button onClick={submitMethod} data-testid="method-submit">Save method</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
